import { Toolbar, Typography } from "@mui/material";
import { Box } from "@mui/system";
import { createElement, useState } from "react";
import DataGrid from "../DataGrid";
import ActionsCell from "../ActionsCell";
import { CreateButton } from "../Buttons";
import DeleteConfimrationModal from "../DeleteConfirmationModal";
import PropTypes from "prop-types";

/**
 * EntityTable component for displaying and managing entities in a table format.
 *
 * Props:
 * @param {Array<Object>} data - Array of entity objects to display. Each entity must have an 'id' property.
 * @param {Array<Object>} columns - Array of column definitions for the DataGrid. Each column should have 'field', 'headerName', and optional 'minWidth', 'disableColumnMenu', 'sortable', 'renderCell'.
 * @param {boolean} [enableCreateEntity=false] - If true, shows a button to create a new entity.
 * @param {boolean} [enableEditEntity=false] - If true, enables editing entities via the editor component.
 * @param {boolean} [enableDeleteEntity=false] - If true, enables deleting entities with confirmation modal.
 * @param {React.ElementType} [EditorComponent] - React component used for editing/creating entities. Receives props: onClose, onSuccess, onError, editedEntity, submitAction, existingEntities, ...editorProps.
 * @param {function} [onCellClick] - Callback fired when a table cell is clicked. Receives cell id.
 * @param {string} [noEntitiesMessage="No entities available."] - Message shown when there are no entities in the table.
 * @param {Object} [dataGridProps={}] - Additional props passed to the DataGrid component.
 * @param {Object} [editorProps={}] - Additional props passed to the EditorComponent.
 * @param {function} [createEntityAction] - Function to handle entity creation. Called by EditorComponent.
 * @param {function} [updateEntityAction] - Function to handle entity update. Called by EditorComponent.
 * @param {function} [deleteEntityAction] - Function to handle entity deletion. Called when confirming delete.
 * @param {function} [onEntityCreateSuccess] - Callback for successful entity creation. Receives created entity.
 * @param {function} [onEntityUpdateSuccess] - Callback for successful entity update. Receives updated entity.
 * @param {function} [onEntityDeleteSuccess] - Callback for successful entity deletion. Receives deleted entity id.
 * @param {string} [deleteConfirmationMessage] - Message shown in the delete confirmation modal.
 * @param {function} [onError] - Callback for handling errors in the editor component.
 * @param {function} [customGridActions] - Optional function to render custom actions in the actions cell.
 */
const EntityTable = (props) => {
    const { data, columns, enableCreateEntity, enableEditEntity, enableDeleteEntity, EditorComponent, onCellClick, noEntitiesMessage, dataGridProps, editorProps, createEntityAction, updateEntityAction, deleteEntityAction, onEntityUpdateSuccess, onEntityCreateSuccess, onEntityDeleteSuccess, deleteConfirmationMessage, onError, customGridActions } = props;

    let _columns = columns;
    if (enableEditEntity || enableDeleteEntity) {
        _columns = [
            ...columns,
            {
                field: 'actions',
                headerName: '',
                minWidth: 200,
                disableColumnMenu: true,
                sortable: false,
                renderCell: (params) => {
                    return <ActionsCell
                        entityId={params.row.id}
                        onEditClick={onEditClick}
                        onDeleteClick={onDeleteClick}
                        customActions={customGridActions}
                    />
                }
            }
        ]
    }

    let [editorOpen, setEditorOpen] = useState(false);
    let [editedEntity, setEditedEntity] = useState(null);
    let [deletedEntity, setDeletedEntity] = useState(null);

    const onAddButtonClick = () => {
        setEditedEntity(null);
        setEditorOpen(true);
    };

    const onEditorSuccess = (entity) => {
        setEditorOpen(false);

        if (editedEntity) {
            onEntityUpdateSuccess(entity);
        }
        else {
            onEntityCreateSuccess(entity);
        }
    }

    const onEditClick = (entityId) => {
        const entity = data.filter(entity => entity.id === entityId)[0];

        setEditedEntity(entity);
        setEditorOpen(true);
    }

    const onDeleteClick = (entityId) => {
        const entity = data.find((entity) => entity.id === entityId);
        setDeletedEntity(entity);
    }

    const onDeleteConfirm = async (entityId) => {
        setDeletedEntity(null);
        try {
            await deleteEntityAction(entityId);
            onEntityDeleteSuccess(entityId);
        }
        catch (error) {
            onError(error)
        }
    }

    const editor = EditorComponent && createElement(
        EditorComponent,
        {
            onClose: () => setEditorOpen(false),
            onSuccess: onEditorSuccess,
            onError: onError,
            editedEntity: editedEntity,
            submitAction: editedEntity ? updateEntityAction : createEntityAction,
            existingEntities: data,
            ...editorProps
        },
        null
    );

    if (!data) {
        return null;
    }

    let table = null;

    if (!data.length) {
        table = <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Typography
                variant="h6"
                component="div"
                sx={{ margin: '20px', textAlign: 'center' }}
            >
                {noEntitiesMessage}
            </Typography>
            {enableCreateEntity && <CreateButton sx={{ width: '300px', height: '50px', alignSelf: 'center' }} onClick={onAddButtonClick} text='Add' />}
        </Box>;
    }
    else {
        table = <Box sx={{ display: 'flex', flexDirection: 'column', maxHeight: '1000px' }}>
            <Toolbar>
                <Box sx={{ flexGrow: 1 }} />
                {enableCreateEntity && <CreateButton onClick={onAddButtonClick} text='Add' />}
            </Toolbar>
            <DataGrid
                rows={data}
                columns={_columns}
                autoHeight
                disableSelectionOnClick={true}
                onCellClick={(cell) => onCellClick && onCellClick(cell.id)}
                sx={{
                    cursor: onCellClick ? 'pointer' : 'unset'
                }}
                {...dataGridProps}
            />
        </Box>
    }

    return <>
        {table}
        {editor && editorOpen && editor}
        <DeleteConfimrationModal entity={deletedEntity} onCancel={() => setDeletedEntity(null)} onConfirm={onDeleteConfirm} message={deleteConfirmationMessage} />
    </>;
}

EntityTable.propTypes = {
    data: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
            // Add other entity properties here if needed
        })
    ).isRequired,
    columns: PropTypes.arrayOf(
        PropTypes.shape({
            field: PropTypes.string.isRequired,
            headerName: PropTypes.string.isRequired,
            minWidth: PropTypes.number,
            disableColumnMenu: PropTypes.bool,
            sortable: PropTypes.bool,
            renderCell: PropTypes.func, // Function to render custom cell content
        })
    ).isRequired,
    enableCreateEntity: PropTypes.bool,
    enableEditEntity: PropTypes.bool,
    enableDeleteEntity: PropTypes.bool,
    EditorComponent: PropTypes.elementType, // React component type for the editor
    onCellClick: PropTypes.func,
    noEntitiesMessage: PropTypes.string,
    dataGridProps: PropTypes.object, // Additional props for the DataGrid component
    editorProps: PropTypes.object, // Additional props for the EditorComponent
    createEntityAction: PropTypes.func, // Function to handle entity creation
    updateEntityAction: PropTypes.func, // Function to handle entity update
    deleteEntityAction: PropTypes.func, // Function to handle entity deletion
    onEntityCreateSuccess: PropTypes.func, // Callback for successful create/update
    onEntityUpdateSuccess: PropTypes.func, // Callback for successful create/update
    onDeleteEntitySuccess: PropTypes.func, // Callback for successful deletion
};

EntityTable.defaultProps = {
    enableCreateEntity: false,
    enableEditEntity: false,
    enableDeleteEntity: false,
    noEntitiesMessage: "No entities available.",
    dataGridProps: {},
    editorProps: {},
};

export default EntityTable;